import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

export default function AIInsiderPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [isSubNavSticky, setIsSubNavSticky] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 160; // メインヘッダー + サブナビの高さ
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      // サブナビゲーションをスティッキーにする判定
      if (window.scrollY > 80) {
        setIsSubNavSticky(true);
      } else {
        setIsSubNavSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/aiinsider-contact-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'AI Insiderは候補者の合否・評価に使えますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '使えません。候補者の不安や迷いを整理し、納得して応募・選考に進める状態をつくる「意思決定支援」のための対話AIです。合否判定や評価用途には用いない前提で設計します。',
        },
      },
      {
        '@type': 'Question',
        name: '「パーソナライズされた情報提供」とは何ですか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '候補者の関心、重視点などに合わせて、同じ質問でも"最適な切り口と順番"で情報を返すことです。一般論の羅列ではなく、意思決定に必要な情報に辿り着ける会話導線を設計します。',
        },
      },
      {
        '@type': 'Question',
        name: '対話ログは企業側でどう活用されますか？個人情報は大丈夫ですか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '企業は「候補者体験の改善」を目的として対話ログを活用します。個人情報の取り扱いは貴社方針に合わせて設計し、必要最小限の取得・保存・閲覧権限で運用します。',
        },
      },
      {
        '@type': 'Question',
        name: '立ち上げまでにどれくらいかかりますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '目安は 2〜4週間です。貴社の状況に合わせて、設置場所・情報整備・関係者テストの範囲を調整し、最短で公開できる形に落とし込みます。',
        },
      },
      {
        '@type': 'Question',
        name: '導入の流れを教えてください。',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '基本は以下の4ステップです。Step1 立ち上げ：データベースの初期構築 / Step2 設置：採用サイト／職種LPに配置 / Step3 公開：関係者テスト→本番ローンチ / Step4 運行：ログ分析→改善サイクル',
        },
      },
      {
        '@type': 'Question',
        name: '月次運用では何をするのですか？("保守"ではない？)',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '月次運用は"壊れないように維持する保守"ではなく、対話ログを起点に品質を上げ続ける改善です。具体的には、ログを見てルールや情報を更新します。',
        },
      },
      {
        '@type': 'Question',
        name: 'どんなKPIで成果を測りますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '代表的には以下です。主要KPI：応募完了率 / 拡張KPI：問い合わせ件数／離脱率',
        },
      },
    ],
  };

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'AI Insider',
    description:
      '応募直前の離脱を防ぐ採用専用AIチャット。リクルーターのような対話で候補者の不安に寄り添い、納得して応募できる状態をつくります。',
    brand: {
      '@type': 'Brand',
      name: 'dot Blue',
    },
    url: `${siteUrl}/ai-insider`,
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'JPY',
      availability: 'https://schema.org/InStock',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'AI Insider｜応募コンバージョンを高める対話型採用AI',
    description:
      'AI Insiderは応募直前の離脱を防ぐ採用専用AIチャットボット。リクルーターのように対話で不安を解消し、エントリー数を最大化。ログ分析による採用課題の可視化も実現します。【無料相談受付中】',
    url: `${siteUrl}/ai-insider`,
    publisher: {
      '@type': 'Organization',
      name: 'dot Blue',
      url: siteUrl,
    },
  };

  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [webPageSchema, productSchema, faqSchema],
  };

  return (
    <>
      <SEO
        title="AI Insider｜採用サイトの離脱を防ぐ対話型AIチャットボット"
        description="採用サイトに来た候補者の「応募直前の離脱」を防ぐAIチャットボットです。リクルーターのように対話で不安を解消し、エントリー数を最大化。ログ分析による採用課題の可視化も実現します。【無料相談受付中】"
        keywords="AI Insider, 採用AI, 候補者体験, 対話AI, 応募完了率, 採用サイト, 職種LP, エンゲージメント, 採用コンバージョン, ログ分析"
        ogImage={`${siteUrl}/og-image-ai-insider.jpg`}
        schema={combinedSchema}
      />
      <div className="min-h-screen bg-[#F5F5F2]">
        {/* Main Header - Global Navigation */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-between h-16 sm:h-20">
              {/* Logo */}
              <Link to="/" className="flex items-center">
                <img 
                  src="https://static.readdy.ai/image/9d74872511a6806a1ddbaf05c38ab433/5a08c197f8230028d9ecca8183376b65.png"
                  alt="dot Blue"
                  className="h-16 w-auto drop-shadow-md"
                />
              </Link>

              {/* Center Navigation */}
              <nav className="hidden lg:flex items-center gap-8">
                <Link
                  to="/"
                  className="text-sm font-bold transition-all whitespace-nowrap cursor-pointer drop-shadow-sm text-[#0B1B33]/70 hover:text-[#0B1B33]"
                >
                  TOP
                </Link>
                <Link
                  to="/ai-insider"
                  className="text-sm font-bold transition-all whitespace-nowrap cursor-pointer drop-shadow-sm text-[#0B1B33] border-b-2 border-[#2563EB]"
                >
                  AI Insider
                </Link>
                <Link
                  to="/ai-development"
                  className="text-sm font-bold transition-all whitespace-nowrap cursor-pointer drop-shadow-sm text-[#0B1B33]/70 hover:text-[#0B1B33]"
                >
                  AI×DX開発
                </Link>
              </nav>

              {/* Mobile Menu */}
              <div className="lg:hidden">
                <div className="flex gap-4">
                  <Link
                    to="/"
                    className="text-xs font-medium transition-colors whitespace-nowrap pb-1 cursor-pointer text-[#6B7280]"
                  >
                    TOP
                  </Link>
                  <Link
                    to="/ai-insider"
                    className="text-xs font-medium transition-colors whitespace-nowrap pb-1 cursor-pointer text-[#0B1B33] border-b-2 border-[#2563EB]"
                  >
                    AI Insider
                  </Link>
                  <Link
                    to="/ai-development"
                    className="text-xs font-medium transition-colors whitespace-nowrap pb-1 cursor-pointer text-[#6B7280]"
                  >
                    AI×DX開発
                  </Link>
                </div>
              </div>

              <button
                onClick={() => scrollToSection('aiinsider-contact')}
                className="hidden lg:inline-block px-4 sm:px-6 py-2 sm:py-2.5 bg-[#FF9900] text-white text-xs sm:text-sm md:text-base font-semibold rounded-xl hover:bg-[#E68A00] transition-colors whitespace-nowrap cursor-pointer shadow-[0_6px_20px_0_rgba(0,0,0,0.08)] focus:ring-2 focus:ring-[rgba(37,99,235,0.35)] focus:outline-none"
              >
                無料で相談する
              </button>
            </div>
          </div>
        </header>

        {/* Sub Navigation - AI Insider Page Only */}
        <div className={`fixed left-0 right-0 z-40 transition-all duration-300 ${isSubNavSticky ? 'top-16 sm:top-20' : 'top-16 sm:top-20'} bg-white/95 backdrop-blur-md border-b border-[#E5E7EB] shadow-sm`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <nav className="flex items-center justify-center gap-4 sm:gap-6 lg:gap-8 h-12 sm:h-14 overflow-x-auto">
              <button
                onClick={() => scrollToSection('problem')}
                className="text-xs sm:text-sm font-medium text-[#44464A] hover:text-[#FF9900] transition-colors whitespace-nowrap cursor-pointer"
              >
                課題
              </button>
              <button
                onClick={() => scrollToSection('trust')}
                className="text-xs sm:text-sm font-medium text-[#44464A] hover:text-[#FF9900] transition-colors whitespace-nowrap cursor-pointer"
              >
                特徴
              </button>
              <button
                onClick={() => scrollToSection('how')}
                className="text-xs sm:text-sm font-medium text-[#44464A] hover:text-[#FF9900] transition-colors whitespace-nowrap cursor-pointer"
              >
                活用例
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="text-xs sm:text-sm font-medium text-[#44464A] hover:text-[#FF9900] transition-colors whitespace-nowrap cursor-pointer"
              >
                料金
              </button>
              <button
                onClick={() => scrollToSection('faq')}
                className="text-xs sm:text-sm font-medium text-[#44464A] hover:text-[#FF9900] transition-colors whitespace-nowrap cursor-pointer"
              >
                FAQ
              </button>
            </nav>
          </div>
        </div>

        <div className="pt-28 sm:pt-36">
          {/* Hero Section */}
          <section id="hero" className="py-8 sm:py-12 lg:py-16 xl:py-20 bg-white px-4 sm:px-6">
            <div className="max-w-7xl mx-auto relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
                <div>
                  <h1 className="mb-4 sm:mb-6 lg:mb-8 leading-tight">
                    <span className="text-2xl sm:text-3xl md:text-4xl lg:text-[3.9rem] font-bold text-[#0B0B0C] block">
                      AI Insider
                    </span>
                    <span className="text-sm sm:text-base md:text-lg lg:text-[1.4rem] font-bold text-[#0B0B0C] block mt-2">
                      評価しない、味方になる。
                    </span>
                  </h1>
                  <p className="text-xs sm:text-sm md:text-base lg:text-xl text-[#0B0B0C] mb-6 sm:mb-8 lg:mb-12 leading-relaxed">
                    採用コンバージョンを高める「意思決定支援」AI<br />
                    多くの候補者が、不安や疑問を抱えたまま立ち止まっています。<br />
                    AI Insiderは、候補者の本音に寄り添う対話で「最後の迷い」を<br />
                    ほどき、納得感のある応募を後押しします。
                  </p>
                  <button
                    onClick={() => scrollToSection('aiinsider-contact')}
                    className="px-6 sm:px-8 lg:px-10 py-2.5 sm:py-3 lg:py-4 bg-[#FF9900] text-white text-xs sm:text-sm md:text-base font-semibold rounded-xl hover:bg-[#E68A00] transition-colors whitespace-nowrap cursor-pointer shadow-[0_6px_20px_0_rgba(0,0,0,0.08)] focus:ring-2 focus:ring-[rgba(37,99,235,0.35)] focus:outline-none"
                  >
                    無料で相談する
                  </button>
                </div>
                <div className="aspect-[4/3] overflow-hidden rounded-lg">
                  <img
                    src="https://readdy.ai/api/search-image?query=Bright%20modern%20recruitment%20office%20scene%20with%20natural%20sunlight%20streaming%20through%20windows%2C%20HR%20professional%20having%20friendly%20conversation%20with%20job%20candidate%2C%20warm%20welcoming%20atmosphere%2C%20clean%20minimalist%20corporate%20interior%2C%20professional%20yet%20approachable%20setting%20%2C%20contemporary%20business%20environment%20with%20soft%20lighting%20and%20comfortable%20seating%20area&width=800&height=600&seq=aiinsider001&orientation=landscape"
                    alt="採用シーン"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Problem Section */}
          <section id="problem" className="py-12 sm:py-16 lg:py-20 xl:py-24 px-4 sm:px-6 relative overflow-hidden">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Young%20professional%20sitting%20by%20office%20window%20contemplating%20career%20decision%2C%20thoughtful%20business%20person%20deep%20in%20reflection%20about%20job%20opportunities%2C%20worried%20employee%20considering%20future%20career%20path%2C%20modern%20office%20setting%20with%20natural%20lighting%2C%20person%20looking%20concerned%20while%20reviewing%20documents%2C%20contemplative%20workplace%20atmosphere%20with%20soft%20natural%20light&width=1920&height=1080&seq=thoughtful001&orientation=landscape"
                alt="悩んでいる若手ビジネスパーソン"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-white/85"></div>
            </div>

            <div className="max-w-6xl mx-auto relative z-10">
              <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-[2.1rem] font-bold text-[#0B0B0C] mb-4 sm:mb-6">
                  候補者が応募をためらう「3つの壁」
                </h2>
                <div className="w-16 h-0.5 bg-[#2563EB] mx-auto mb-6 sm:mb-8"></div>
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl text-[#0B0B0C] font-bold mb-6 sm:mb-8 lg:mb-12 text-center leading-relaxed">
                  多くの候補者が"応募ボタンの前"で迷って離脱します
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                <div className="bg-white/70 backdrop-blur-md p-4 sm:p-6 lg:p-8 rounded-3xl border border-white/20 shadow-[0_8px_32px_rgba(31,38,135,0.37)] hover:shadow-[0_12px_48px_rgba(31,38,135,0.5)] transition-all relative">
                  <div className="flex items-center justify-center mb-3 sm:mb-4 lg:mb-6">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 flex items-center justify-center bg-[#EAF2FF] rounded-full">
                      <i className="ri-search-eye-line text-xl sm:text-2xl lg:text-3xl text-[#2563EB]"></i>
                    </div>
                  </div>
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-[#0B0B0C] mb-2 sm:mb-3 text-center">
                    「リアル」が見えない
                  </h3>
                  <p className="text-xs sm:text-sm text-[#555555] leading-relaxed text-center">
                    求人票などの「綺麗な公式情報」だけでは、実際の働き方や空気感が伝わらず、応募へ踏み切れません。
                  </p>
                </div>

                <div className="bg-white/70 backdrop-blur-md p-4 sm:p-6 lg:p-8 rounded-3xl border border-white/20 shadow-[0_8px_32px_rgba(31,38,135,0.37)] hover:shadow-[0_12px_48px_rgba(31,38,135,0.5)] transition-all relative">
                  <div className="flex items-center justify-center mb-3 sm:mb-4 lg:mb-6">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 flex items-center justify-center bg-[#EAF2FF] rounded-full">
                      <i className="ri-chat-quote-line text-xl sm:text-2xl lg:text-3xl text-[#2563EB]"></i>
                    </div>
                  </div>
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-[#0B0B0C] mb-2 sm:mb-3 text-center">
                    「本音」が聞けない
                  </h3>
                  <p className="text-xs sm:text-sm text-[#555555] leading-relaxed text-center">
                    「残業は？人間関係は？」評価を気にして、本当に聞きたい質問ほど飲み込んでしまいます。
                  </p>
                </div>

                <div className="bg-white/70 backdrop-blur-md p-4 sm:p-6 lg:p-8 rounded-3xl border border-white/20 shadow-[0_8px_32px_rgba(31,38,135,0.37)] hover:shadow-[0_12px_48px_rgba(31,38,135,0.5)] transition-all sm:col-span-2 lg:col-span-1 relative">
                  <div className="flex items-center justify-center mb-3 sm:mb-4 lg:mb-6">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 flex items-center justify-center bg-[#EAF2FF] rounded-full">
                      <i className="ri-stack-line text-xl sm:text-2xl lg:text-3xl text-[#2563EB]"></i>
                    </div>
                  </div>
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-[#0B0B0C] mb-2 sm:mb-3 text-center">
                    「面談」は重すぎる
                  </h3>
                  <p className="text-xs sm:text-sm text-[#555555] leading-relaxed text-center">
                    「ちょっと聞きたいだけ」なのに、わざわざカジュアル面談を申し込むのは、心理的なハードルが高すぎます。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Solution Definition Section - Unified from Comparison and Overview */}
          <section className="py-12 sm:py-16 lg:py-20 xl:py-24 px-4 sm:px-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-[450px] h-[450px] rounded-full bg-[#EAF2FF] opacity-8 blur-3xl -translate-x-1/3"></div>

            <div className="max-w-6xl mx-auto relative z-10">
              <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-[2.1rem] font-bold text-[#0B0B0C] mb-4 sm:mb-6">
                  面談の「温かみ」と、ボットの「気楽さ」を両立する
                </h2>
                <div className="w-16 h-0.5 bg-[#2563EB] mx-auto mb-6 sm:mb-8"></div>
                <p className="text-sm sm:text-base md:text-lg lg:text-xl text-[#0B0B0C] leading-relaxed max-w-4xl mx-auto">
                  候補者が求めているのは、評価を気にせず話せる「安全地帯」です。<br />
                  AI Insiderは、既存の採用手法が抱えるジレンマを解消する「第3の選択肢」です。
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12 lg:mb-16">
                <div className="bg-white p-4 sm:p-6 lg:p-10 rounded-lg border border-[#E5E7EB]">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#EAF2FF] rounded-full">
                      <i className="ri-user-line text-2xl sm:text-3xl text-[#2563EB]"></i>
                    </div>
                  </div>
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold mb-3 sm:mb-4 lg:mb-6 text-[#0B0B0C] text-center">
                    vs カジュアル面談
                  </h3>
                  <h4 className="text-xs sm:text-sm md:text-base font-bold text-[#2563EB] mb-2 sm:mb-3 text-center">
                    マンパワーの限界を超える
                  </h4>
                  <p className="text-xs sm:text-sm text-[#44464A] leading-relaxed text-center">
                    面談は有効ですが、全候補者への対応は物理的に不可能です。AIなら、24時間365日、何千人来ても一人ひとりに「個別の対話」を提供できます。
                  </p>
                </div>

                <div className="bg-white p-4 sm:p-6 lg:p-10 rounded-lg border border-[#E5E7EB]">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#EAF2FF] rounded-full">
                      <i className="ri-robot-line text-2xl sm:text-3xl text-[#2563EB]"></i>
                    </div>
                  </div>
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold mb-3 sm:mb-4 lg:mb-6 text-[#0B0B0C] text-center">
                    vs 従来のチャットボット
                  </h3>
                  <h4 className="text-xs sm:text-sm md:text-base font-bold text-[#2563EB] mb-2 sm:mb-3 text-center">
                    無機質なQ&Aからの脱却
                  </h4>
                  <p className="text-xs sm:text-sm text-[#44464A] leading-relaxed text-center">
                    「あるある」な質問に答えるだけではありません。候補者の文脈を理解し、企業の温度感を乗せた「口説き」を行う点で、従来のボットとは一線を画します。
                  </p>
                </div>

                <div className="bg-[#EAF2FF] p-4 sm:p-6 lg:p-10 rounded-lg border border-[#2563EB]/20 md:col-span-2 lg:col-span-1">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#2563EB] rounded-full">
                      <i className="ri-shield-check-line text-2xl sm:text-3xl text-white"></i>
                    </div>
                  </div>
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold mb-3 sm:mb-4 lg:mb-6 text-[#0B0B0C] text-center">
                    AI Insiderの定義
                  </h3>
                  <h4 className="text-xs sm:text-sm md:text-base font-bold text-[#2563EB] mb-2 sm:mb-3 text-center">
                    「評価しない」という新常識
                  </h4>
                  <p className="text-xs sm:text-sm text-[#44464A] leading-relaxed text-center">
                    企業側の選考意図を完全に排除。候補者の「味方」として振る舞うことで、人間には決して話せない本音を引き出し、深い納得感を生み出します。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Value Section */}
          <section id="trust" className="py-12 sm:py-16 lg:py-20 xl:py-24 px-4 sm:px-6 bg-[#F5F5F2] relative overflow-hidden">
            <div className="absolute bottom-0 left-0 w-[450px] h-[450px] rounded-full bg-[#EAF2FF] opacity-8 blur-3xl -translate-x-1/3 translate-y-1/4"></div>

            <div className="max-w-6xl mx-auto relative z-10">
              <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-[2.1rem] font-bold text-[#0B0B0C] mb-4">
                  AI Insiderがもたらす3つの価値
                </h2>
                <div className="w-16 h-0.5 bg-[#2563EB] mx-auto"></div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg border border-[#E5E7EB] relative">
                  <div className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 w-8 h-8 sm:w-10 sm:h-10 bg-[#2563EB] text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-lg">
                    1
                  </div>
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-[#0B0B0C] mb-2 sm:mb-3 lg:mb-4 text-center">
                    採用成果の向上
                  </h3>
                  <p className="text-xs sm:text-sm text-[#44464A] leading-relaxed">
                    応募〜承諾のコンバージョン増加
                  </p>
                </div>

                <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg border border-[#E5E7EB] relative">
                  <div className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 w-8 h-8 sm:w-10 sm:h-10 bg-[#2563EB] text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-lg">
                    2
                  </div>
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-[#0B0B0C] mb-2 sm:mb-3 lg:mb-4 text-center">
                    採用工数の最適化
                  </h3>
                  <p className="text-xs sm:text-sm text-[#44464A] leading-relaxed">
                    魅力付け対応をAIが担い、より大きい母集団形成
                  </p>
                </div>

                <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg border border-[#E5E7EB] sm:col-span-2 lg:col-span-1 relative">
                  <div className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 w-8 h-8 sm:w-10 sm:h-10 bg-[#2563EB] text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-lg">
                    3
                  </div>
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-[#0B0B0C] mb-2 sm:mb-3 lg:mb-4 text-center">
                    候補者の本音をデータ化
                  </h3>
                  <p className="text-xs sm:text-sm text-[#44464A] leading-relaxed">
                    対話ログが求人票/面接/訴求の改善材料になる
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* How Section - Before/After Comparison */}
          <section id="how" className="py-12 sm:py-16 lg:py-20 xl:py-24 px-4 sm:px-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#EAF2FF] opacity-10 blur-3xl translate-x-1/4 -translate-y-1/4"></div>

            <div className="max-w-6xl mx-auto relative z-10">
              <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-[2.1rem] font-bold text-[#0B0B0C] mb-4">
                  なぜ、候補者の心が動くのか
                </h2>
                <div className="w-16 h-0.5 bg-[#2563EB] mx-auto mb-4 sm:mb-6"></div>
                <p className="text-sm sm:text-base md:text-lg lg:text-xl text-[#0B0B0C] leading-relaxed">
                  マニュアル通りの「回答」ではなく、文脈を汲んだ「対話」を。
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4 lg:gap-6 items-start max-w-5xl mx-auto">
                {/* Left: Traditional Bot */}
                <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg border border-[#E5E7EB]">
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-[#0B0B0C] mb-3 sm:mb-4 text-center">
                    従来のFAQボット
                  </h3>
                  <p className="text-xs sm:text-sm text-[#C2410C] font-medium mb-3 sm:mb-4 text-center">
                    定型文の提示のみ（会話が続かない）
                  </p>

                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex justify-end">
                      <div className="bg-[#EAF2FF] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg max-w-[90%]">
                        <p className="text-xs text-[#0B0B0C]">
                          子育てとの両立は現実的ですか？残業の実態が気になります…
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-start">
                      <div className="bg-[#F5F5F5] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg max-w-[90%]">
                        <p className="text-xs text-[#0B0B0C]">
                          全社の月平均残業時間は25時間前後です。また、育児短時間勤務制度の利用実績もございます。詳細は採用サイトの「福利厚生」ページをご確認ください。
                        </p>
                      </div>
                    </div>
                  </div>

                  <p className="text-[0.6rem] text-[#C2410C] mt-2 sm:mt-3 text-center">
                    ※質問に答えているが、共感がなく会話が終了している
                  </p>
                </div>

                {/* Center: VS */}
                <div className="flex items-center justify-center lg:py-16">
                  <div className="w-10 h-10 sm:w-14 sm:h-14 bg-[#2563EB] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xs sm:text-sm">VS</span>
                  </div>
                </div>

                {/* Right: AI Insider */}
                <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg border border-[#2563EB]/20 ring-2 ring-[#2563EB]/10">
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-[#0B0B0C] mb-3 sm:mb-4 text-center">
                    AI Insider
                  </h3>
                  <p className="text-xs sm:text-sm text-[#2563EB] font-medium mb-3 sm:mb-4 text-center">
                    共感と深堀で不安を解消
                  </p>

                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex justify-end">
                      <div className="bg-[#EAF2FF] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg max-w-[90%]">
                        <p className="text-xs text-[#0B0B0C]">
                          子育てとの両立は現実的ですか？残業の実態が気になります…
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-start">
                      <div className="bg-[#F5F5F5] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg max-w-[90%]">
                        <p className="text-xs text-[#0B0B0C]">その点、気になりますよね。</p>
                      </div>
                    </div>

                    <div className="flex justify-start">
                      <div className="bg-[#F5F5F5] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg max-w-[90%]">
                        <p className="text-xs text-[#0B0B0C]">長く働く上で、とても重要なポイントだと思います。</p>
                      </div>
                    </div>

                    <div className="flex justify-start">
                      <div className="bg-[#F5F5F5] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg max-w-[90%]">
                        <p className="text-xs text-[#0B0B0C]">
                          全社の月平均残業は25時間前後ですが、部署や時期によって波があります。特に気になっているのは、時間の総量でしょうか？それとも中抜けなどの柔軟性でしょうか？
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <div className="bg-[#EAF2FF] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg max-w-[90%]">
                        <p className="text-xs text-[#0B0B0C]">子供がいるので、急な迎えや中抜けができるかが一番心配です。</p>
                      </div>
                    </div>

                    <div className="flex justify-start">
                      <div className="bg-[#F5F5F5] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg max-w-[90%]">
                        <p className="text-xs text-[#0B0B0C]">なるほど。その点では、営業部のBさんのケースが参考になるかもしれません。</p>
                      </div>
                    </div>

                    <div className="flex justify-start">
                      <div className="bg-[#F5F5F5] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg max-w-[90%]">
                        <p className="text-xs text-[#0B0B0C]">Bさんも小学生のお子さんがいて『毎日17時台に一度抜ける』働き方をしています。</p>
                      </div>
                    </div>
                  </div>

                  <p className="text-[0.6rem] text-[#44464A] mt-2 sm:mt-3 text-center">
                    ※会話例はイメージです。実装時は貴社の情報に合わせて設計します。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Data Section */}
          <section className="py-12 sm:py-16 lg:py-20 xl:py-24 px-4 sm:px-6 bg-[#F5F5F2] relative overflow-hidden">
            <div className="absolute top-1/2 right-0 w-[500px] h-[500px] rounded-full bg-[#EAF2FF] opacity-10 blur-3xl translate-x-1/4 -translate-y-1/2"></div>

            <div className="max-w-7xl mx-auto relative z-10">
              <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-[2.1rem] font-bold text-[#0B0B0C] mb-4">
                  AI Insiderのデータ資産が、「GEO対策」に直結する
                </h2>
                <div className="w-16 h-0.5 bg-[#2563EB] mx-auto mb-4 sm:mb-6"></div>
                <p className="text-sm sm:text-base md:text-lg text-[#0B0B0C] leading-relaxed max-w-4xl mx-auto">
                  構築した対話データは、自社サイトだけでなく、外部AI検索の「公式な正解」としても機能します。
                </p>
              </div>

              {/* 3-Stage Logical Flow */}
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-4 sm:gap-6 lg:gap-4 items-center max-w-6xl mx-auto">
                {/* BOX 1: Data Asset */}
                <div className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 xl:p-10 min-h-[200px] sm:min-h-[250px] lg:min-h-[280px] flex flex-col items-center justify-center border border-[#E5E7EB] shadow-lg">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mb-4 sm:mb-6">
                    <i className="ri-database-2-line text-3xl sm:text-4xl lg:text-5xl text-[#2563EB]"></i>
                  </div>
                  
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-[#0B0B0C] mb-2 sm:mb-3 lg:mb-4 text-center">
                    社内情報の「構造化」
                  </h3>
                  
                  <p className="text-xs sm:text-sm text-[#44464A] leading-relaxed text-center">
                    AI Insiderの導入プロセスで、散らばる社内情報を整理。曖昧な企業情報をAIが理解可能な「構造化データ」へ変換します。
                  </p>
                </div>

                {/* Arrow 1 with Label */}
                <div className="hidden lg:flex flex-col items-center justify-center">
                  <i className="ri-arrow-right-line text-3xl sm:text-4xl text-[#2563EB] font-bold mb-1 sm:mb-2"></i>
                  <p className="text-xs text-[#2563EB] font-medium whitespace-nowrap">このデータが基盤となり...</p>
                </div>

                {/* BOX 2: Governance */}
                <div className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 xl:p-10 min-h-[200px] sm:min-h-[250px] lg:min-h-[280px] flex flex-col items-center justify-center border border-[#E5E7EB] shadow-lg">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mb-4 sm:mb-6">
                    <i className="ri-shield-check-line text-3xl sm:text-4xl lg:text-5xl text-[#2563EB]"></i>
                  </div>
                  
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-[#0B0B0C] mb-2 sm:mb-3 lg:mb-4 text-center">
                    唯一の「正解ソース」
                  </h3>
                  
                  <p className="text-xs sm:text-sm text-[#44464A] leading-relaxed text-center">
                    整理された情報を、企業の「公式データベース」として確立。社内外に対し、「これが正解」と定義する中心点となります。
                  </p>
                </div>

                {/* Arrow 2 with Label */}
                <div className="hidden lg:flex flex-col items-center justify-center">
                  <i className="ri-arrow-right-line text-3xl sm:text-4xl text-[#2563EB] font-bold mb-1 sm:mb-2"></i>
                  <p className="text-xs text-[#2563EB] font-medium whitespace-nowrap">2つの成果を生む</p>
                </div>

                {/* BOX 3: Dual Utilization */}
                <div className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 xl:p-10 min-h-[200px] sm:min-h-[250px] lg:min-h-[280px] flex flex-col items-center justify-center border border-[#E5E7EB] shadow-lg">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mb-4 sm:mb-6">
                    <i className="ri-global-line text-3xl sm:text-4xl lg:text-5xl text-[#2563EB]"></i>
                  </div>
                  
                  <h3 className="text-sm sm:text-base lg:text-lg font-bold text-[#0B0B0C] mb-2 sm:mb-3 lg:mb-4 text-center">
                    自社・外部への「双方向」活用
                  </h3>
                  
                  <p className="text-xs sm:text-sm text-[#44464A] leading-relaxed text-center">
                    自社HPでの対話だけでなく、外部AI(ChatGPT等)へも正しい情報を参照させ、誤った回答（ハルシネーション）を防ぎます。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Placement Section */}
          <section id="ops" className="py-12 sm:py-16 lg:py-20 xl:py-24 px-4 sm:px-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-[450px] h-[450px] rounded-full bg-[#EAF2FF] opacity-8 blur-3xl -translate-x-1/3"></div>

            <div className="max-w-6xl mx-auto relative z-10">
              <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-[2.1rem] font-bold text-[#0B0B0C] mb-4">
                  候補者がいる「すべての場所」が、接点になる
                </h2>
                <div className="w-16 h-0.5 bg-[#2563EB] mx-auto mb-4 sm:mb-6"></div>
                <p className="text-sm sm:text-base md:text-lg text-[#0B0B0C] leading-relaxed max-w-4xl mx-auto">
                  採用サイトから内定者フォローまで。タグを1行埋め込むだけで、あらゆるページが「対話の窓口」に変わります。
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8">
                <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg border border-[#E5E7EB] flex flex-col items-center text-center shadow-lg hover:shadow-xl transition-all">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mb-3 sm:mb-4">
                    <i className="ri-home-line text-2xl sm:text-3xl lg:text-4xl text-[#2563EB]"></i>
                  </div>
                  <h3 className="text-xs sm:text-sm md:text-base font-bold text-[#0B0B0C] mb-2 sm:mb-3">採用サイトTOP</h3>
                  <p className="text-xs text-[#44464A] leading-relaxed">
                    最初の興味喚起と、全体像の案内役として。
                  </p>
                </div>

                <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg border border-[#E5E7EB] flex flex-col items-center text-center shadow-lg hover:shadow-xl transition-all">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mb-3 sm:mb-4">
                    <i className="ri-file-list-line text-2xl sm:text-3xl lg:text-4xl text-[#2563EB]"></i>
                  </div>
                  <h3 className="text-xs sm:text-sm md:text-base font-bold text-[#0B0B0C] mb-2 sm:mb-3">職種別求人ページ</h3>
                  <p className="text-xs text-[#44464A] leading-relaxed">
                    「私にできる？」という個別の不安解消に。
                  </p>
                </div>

                <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg border border-[#E5E7EB] flex flex-col items-center text-center shadow-lg hover:shadow-xl transition-all">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mb-3 sm:mb-4">
                    <i className="ri-article-line text-2xl sm:text-3xl lg:text-4xl text-[#2563EB]"></i>
                  </div>
                  <h3 className="text-xs sm:text-sm md:text-base font-bold text-[#0B0B0C] mb-2 sm:mb-3">オウンドメディア/記事</h3>
                  <p className="text-xs text-[#44464A] leading-relaxed">
                    社員インタビュー等を読んだ後の、感想戦や深掘りに。
                  </p>
                </div>

                <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg border border-[#E5E7EB] flex flex-col items-center text-center shadow-lg hover:shadow-xl transition-all">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mb-3 sm:mb-4">
                    <i className="ri-user-line text-2xl sm:text-3xl lg:text-4xl text-[#2563EB]"></i>
                  </div>
                  <h3 className="text-xs sm:text-sm md:text-base font-bold text-[#0B0B0C] mb-2 sm:mb-3">候補者マイページ</h3>
                  <p className="text-xs text-[#44464A] leading-relaxed">
                    選考中の疑問解消を。
                  </p>
                </div>

                <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg border border-[#E5E7EB] flex flex-col items-center text-center shadow-lg hover:shadow-xl transition-all sm:col-span-2 lg:col-span-1">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mb-3 sm:mb-4">
                    <i className="ri-hand-heart-line text-2xl sm:text-3xl lg:text-4xl text-[#2563EB]"></i>
                  </div>
                  <h3 className="text-xs sm:text-sm md:text-base font-bold text-[#0B0B0C] mb-2 sm:mb-3">内定者専用サイト</h3>
                  <p className="text-xs text-[#44464A] leading-relaxed">
                    承諾前の最後の迷いや入社までのフォローに。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Pricing Section */}
          <section id="pricing" className="py-12 sm:py-16 lg:py-20 xl:py-24 px-4 sm:px-6 bg-[#F5F5F2] relative overflow-hidden">
            <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#EAF2FF] opacity-10 blur-3xl translate-x-1/4 translate-y-1/4"></div>

            <div className="max-w-6xl mx-auto relative z-10">
              <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-[2.1rem] font-bold text-[#0B0B0C] mb-4">
                  Pricing
                </h2>
                <div className="w-16 h-0.5 bg-[#2563EB] mx-auto"></div>
              </div>

              <p className="text-base sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl text-[#0B0B0C] font-bold mb-2 text-center px-4">
                案件ごとにお見積もりします
              </p>
              <p className="text-[0.5rem] sm:text-[0.6rem] text-[#44464A] mb-6 sm:mb-8 lg:mb-12 text-center">
                (目的・範囲・運用体制によって変わります)。
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                <div className="bg-white p-4 sm:p-6 lg:p-10 rounded-lg border border-[#E5E7EB]">
                  <h3 className="text-base sm:text-lg lg:text-xl font-bold mb-2 sm:mb-3 lg:mb-4 text-[#0B0B0C]">
                    初期設計
                  </h3>
                  <p className="text-xs sm:text-sm text-[#44464A] leading-relaxed">
                    現状把握／導線と対話の設計
                  </p>
                </div>

                <div className="bg-white p-4 sm:p-6 lg:p-10 rounded-lg border border-[#E5E7EB]">
                  <h3 className="text-base sm:text-lg lg:text-xl font-bold mb-2 sm:mb-3 lg:mb-4 text-[#0B0B0C]">
                    初期構築
                  </h3>
                  <p className="text-xs sm:text-sm text-[#44464A] leading-relaxed">
                    ナレッジDB整備／設置／関係者テスト
                  </p>
                </div>

                <div className="bg-white p-4 sm:p-6 lg:p-10 rounded-lg border border-[#E5E7EB] sm:col-span-2 lg:col-span-1">
                  <h3 className="text-base sm:text-lg lg:text-xl font-bold mb-2 sm:mb-3 lg:mb-4 text-[#0B0B0C]">
                    月次運用
                  </h3>
                  <p className="text-xs sm:text-sm text-[#44464A] leading-relaxed">
                    ログ分析／改善バックログ／DB更新
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section id="faq" className="py-12 sm:py-16 lg:py-20 xl:py-24 px-4 sm:px-6 relative overflow-hidden">
            <div className="absolute top-1/2 left-0 w-[450px] h-[450px] rounded-full bg-[#EAF2FF] opacity-8 blur-3xl -translate-x-1/3 -translate-y-1/2"></div>

            <div className="max-w-4xl mx-auto relative z-10">
              <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-[2.1rem] font-bold text-[#0B0B0C] mb-4">
                  FAQ
                </h2>
                <div className="w-16 h-0.5 bg-[#2563EB] mx-auto"></div>
              </div>

              <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                {[
                  {
                    q: 'AI Insiderは候補者の合否・評価に使えますか？',
                    a: '使えません。候補者の不安や迷いを整理し、納得して応募・選考に進める状態をつくる「意思決定支援」のための対話AIです。合否判定や評価用途には用いない前提で設計します。',
                  },
                  {
                    q: '「パーソナライズされた情報提供」とは何ですか？',
                    a: '候補者の関心、重視点などに合わせて、同じ質問でも"最適な切り口と順番"で情報を返すことです。一般論の羅列ではなく、意思決定に必要な情報に辿り着ける会話導線を設計します。',
                  },
                  {
                    q: '対話ログは企業側でどう活用されますか？個人情報は大丈夫ですか？',
                    a: '企業は「候補者体験の改善」を目的として対話ログを活用します。個人情報の取り扱いは貴社方針に合わせて設計し、必要最小限の取得・保存・閲覧権限で運用します。',
                  },
                  {
                    q: '立ち上げまでにどれくらいかかりますか？',
                    a: '目安は 2〜4週間です。貴社の状況に合わせて、設置場所・情報整備・関係者テストの範囲を調整し、最短で公開できる形に落とし込みます。',
                  },
                  {
                    q: '導入の流れを教えてください。',
                    a: '基本は以下の4ステップです。Step1 立ち上げ：データベースの初期構築 / Step2 設置：採用サイト／職種LPに配置 / Step3 公開：関係者テスト→本番ローンチ / Step4 運行：ログ分析→改善サイクル',
                  },
                  {
                    q: '月次運用では何をするのですか？("保守"ではない？)',
                    a: '月次運用は"壊れないように維持する保守"ではなく、対話ログを起点に品質を上げ続ける改善です。具体的には、ログを見てルールや情報を更新します。',
                  },
                  {
                    q: 'どんなKPIで成果を測りますか？',
                    a: '代表的には以下です。主要KPI：応募完了率 / 拡張KPI：問い合わせ件数／離脱率',
                  },
                ].map((faq, index) => (
                  <div key={index} className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-3 sm:px-4 lg:px-8 py-3 sm:py-4 lg:py-6 text-left flex items-center justify-between hover:bg-[#FBFAF7] transition-colors cursor-pointer"
                    >
                      <span className="text-xs sm:text-sm font-medium text-[#0B0B0C] pr-2 sm:pr-3 lg:pr-4">{faq.q}</span>
                      <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-sm sm:text-base lg:text-lg text-[#2563EB] flex-shrink-0`}></i>
                    </button>
                    {openFaq === index && (
                      <div className="px-3 sm:px-4 lg:px-8 pb-3 sm:pb-4 lg:pb-6">
                        <p className="text-xs sm:text-sm lg:text-base font-bold text-[#0B0B0C] leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Final CTA + Form Section */}
          <section id="aiinsider-contact" className="py-12 sm:py-16 lg:py-20 xl:py-24 px-4 sm:px-6 bg-[#F5F5F2] relative overflow-hidden">
            <div className="absolute top-0 left-1/2 w-[550px] h-[550px] rounded-full bg-[#EAF2FF] opacity-10 blur-3xl -translate-x-1/2 -translate-y-1/3"></div>

            <div className="max-w-2xl mx-auto relative z-10">
              <div className="text-center mb-6 sm:mb-8 lg:mb-12">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-[2.1rem] font-bold text-[#0B0B0C] mb-4">
                  まずはデモ相談から
                </h2>
                <div className="w-16 h-0.5 bg-[#2563EB] mx-auto"></div>
              </div>

              <p className="text-center text-xs sm:text-sm text-[#44464A] mb-6 sm:mb-8 lg:mb-12 leading-relaxed">
                現状の採用導線と課題意識を確認し、設計・設置・運用の進め方をご提案します。
              </p>

              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 lg:space-y-6" data-readdy-form id="aiinsider-contact-form">
                <div>
                  <label htmlFor="company" className="block text-xs sm:text-sm font-medium text-[#0B0B0C] mb-1.5 sm:mb-2">
                    会社名
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    required
                    className="w-full px-2.5 sm:px-3 lg:px-4 py-2 sm:py-2.5 lg:py-3 border border-[#E5E7EB] rounded-md focus:ring-2 focus:ring-[rgba(37,99,235,0.35)] focus:border-[#2563EB] text-xs sm:text-sm bg-white"
                  />
                </div>

                <div>
                  <label htmlFor="name" className="block text-xs sm:text-sm font-medium text-[#0B0B0C] mb-1.5 sm:mb-2">
                    氏名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-2.5 sm:px-3 lg:px-4 py-2 sm:py-2.5 lg:py-3 border border-[#E5E7EB] rounded-md focus:ring-2 focus:ring-[rgba(37,99,235,0.35)] focus:border-[#2563EB] text-xs sm:text-sm bg-white"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-[#0B0B0C] mb-1.5 sm:mb-2">
                    メール
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-2.5 sm:px-3 lg:px-4 py-2 sm:py-2.5 lg:py-3 border border-[#E5E7EB] rounded-md focus:ring-2 focus:ring-[rgba(37,99,235,0.35)] focus:border-[#2563EB] text-xs sm:text-sm bg-white"
                  />
                </div>

                <div>
                  <label htmlFor="inquiry" className="block text-xs sm:text-sm font-medium text-[#0B0B0C] mb-1.5 sm:mb-2">
                    相談内容
                  </label>
                  <textarea
                    id="inquiry"
                    name="inquiry"
                    rows={4}
                    maxLength={500}
                    required
                    className="w-full px-2.5 sm:px-3 lg:px-4 py-2 sm:py-2.5 lg:py-3 border border-[#E5E7EB] rounded-md focus:ring-2 focus:ring-[rgba(37,99,235,0.35)] focus:border-[#2563EB] resize-none text-xs sm:text-sm bg-white"
                  ></textarea>
                </div>

                <div>
                  <label htmlFor="hiring-range" className="block text-xs sm:text-sm font-medium text-[#0B0B0C] mb-1.5 sm:mb-2">
                    年間採用人数レンジ
                  </label>
                  <select
                    id="hiring-range"
                    name="hiring-range"
                    required
                    className="w-full px-2.5 sm:px-3 lg:px-4 py-2 sm:py-2.5 lg:py-3 border border-[#E5E7EB] rounded-md focus:ring-2 focus:ring-[rgba(37,99,235,0.35)] focus:border-[#2563EB] text-xs sm:text-sm bg-white cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="1-10">1-10名</option>
                    <option value="11-50">11-50名</option>
                    <option value="51-100">51-100名</option>
                    <option value="101-300">101-300名</option>
                    <option value="301+">301名以上</option>
                  </select>
                </div>

                {formStatus === 'success' && (
                  <div className="p-2.5 sm:p-3 lg:p-4 bg-green-50 border border-green-200 rounded-md text-green-800 text-xs sm:text-sm">
                    送信が完了しました。担当が確認し、2営業日以内にご連絡します。
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="p-2.5 sm:p-3 lg:p-4 bg-red-50 border border-red-200 rounded-md text-red-800 text-xs sm:text-sm">
                    送信に失敗しました。もう一度お試しください。
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 bg-[#FF9900] text-white text-xs sm:text-sm md:text-base font-semibold rounded-xl hover:bg-[#E68A00] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_6px_20px_0_rgba(0,0,0,0.08)] focus:ring-2 focus:ring-[rgba(37,99,235,0.35)] focus:outline-none"
                >
                  {formStatus === 'submitting' ? '送信中...' : '無料で相談する'}
                </button>
              </form>

              <p className="text-center text-[0.65rem] sm:text-xs text-[#44464A] mt-3 sm:mt-4 lg:mt-6">
                送信内容は担当が確認し、2営業日以内にご連絡します。
              </p>
            </div>
          </section>

          {/* Footer */}
          <footer className="bg-[#0B0B0C] text-white py-8 sm:py-12 lg:py-16 px-4 sm:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="mb-4 sm:mb-6 lg:mb-8">
                <h3 className="text-xs sm:text-sm font-medium text-[#E5E7EB] mb-2 sm:mb-3 lg:mb-4">リンク</h3>
                <div className="flex flex-wrap gap-3 sm:gap-4 lg:gap-6">
                  <a href="/privacy-policy" className="text-xs sm:text-sm text-[#E5E7EB] hover:text-white transition-colors cursor-pointer hover:underline">
                    プライバシーポリシー
                  </a>
                  <a href="/terms" className="text-xs sm:text-sm text-[#E5E7EB] hover:text-white transition-colors cursor-pointer hover:underline">
                    利用規約
                  </a>
                  <a href="/security" className="text-xs sm:text-sm text-[#E5E7EB] hover:text-white transition-colors cursor-pointer hover:underline">
                    セキュリティ
                  </a>
                  <a href="/company" className="text-xs sm:text-sm text-[#E5E7EB] hover:text-white transition-colors cursor-pointer hover:underline">
                    会社概要
                  </a>
                </div>
              </div>

              <div className="pt-4 sm:pt-6 lg:pt-8 border-t border-[#44464A] flex flex-col lg:flex-row justify-between items-center gap-2 sm:gap-3 lg:gap-4">
                <p className="text-xs sm:text-sm text-[#E5E7EB]">
                  Copyright © dot Blue Inc. All Rights Reserved.
                </p>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
}
